import { Seo, StructuredData } from '@/components/molecules';
import { CompanyStoryBand, ImportPromiseBand, PageIntro, QuoteBand } from '@/components/organisms';
import { introVehicles } from '@/config/introVehicles';
import { type WhatsAppRouteHandle } from '@/hooks/usePageWhatsAppMessage';
import { breadcrumbSchema, faqSchema, organizationSchema } from '@/utils/structuredData';

import {
  CATALOGUE_ID,
  crumbs,
  FAQ_TITLE_ID,
  introHighlights,
  serviceCountWord,
  ServiceCountWord,
  serviceFaq,
  WHATSAPP_MESSAGE,
} from './content';
import {
  ServiceCatalogueBand,
  ServiceCompareBand,
  ServiceFaqBand,
  ServiceSituationsBand,
} from './organisms';

/** Opens WhatsApp with the context of this page (§60 K/L). */
export const handle: WhatsAppRouteHandle = { whatsappMessage: WHATSAPP_MESSAGE };

/**
 * What Kabex Motors actually does — the page a visitor reaches when they know
 * they want a car imported and do not yet know what they are buying from us.
 *
 * The services (§7) were previously shown in two places, and in both of them
 * as a name and an icon: the home page's strip and the /about capability
 * band. That was the whole problem. A one-line label answers "is this on the
 * list" and nothing else — not what the service covers, not what it costs, not
 * whether it can be bought on its own — so a visitor with a real question had
 * nowhere on the site to take it, and services that take real work to deliver
 * were presented as a graphic.
 *
 * It is ordered as the doubt is actually resolved. Here is everything, grouped
 * into the three stages of an import, so you can see where you are. Here is
 * the same set as one table, so you can choose between them on the two things
 * you are actually choosing on — what each leaves you with, and what it costs.
 * Here is your own situation in your own words, so you do not have to
 * translate it into ours. Here is what everyone asks about buying these. And
 * then two ways to get in touch.
 *
 * Three decisions in that order are worth stating, because each was the other
 * way round first:
 *
 *   - The situations band comes *after* the catalogue. A visitor who arrived
 *     from the "Services" menu came to see the list, and a page that opened by
 *     interrogating them about their problem would be answering a question
 *     they had not asked yet.
 *   - The comparison band comes after the catalogue too, not before it. The
 *     catalogue is the page's argument — that an import has an order — and a
 *     table in front of it reduces that to a price list before the reader has
 *     been given the shape. Browse, then decide.
 *   - The catalogue keeps the intro's jump target. `#catalogue` is what the
 *     opening action points at, because "See all seven" promises the thing
 *     with the descriptions in it, not the summary of it.
 *
 * The page holds no state and no logic. Each band is one organism that owns its
 * own `Section`, because two of the three are two-column layouts with sticky
 * heading columns whose proportions belong inside the band rather than above
 * it. Anything that thinks lives in a hook beside the organism that draws it —
 * the grouping and the scroll spy in `useServiceCatalogue`, the accordion in
 * the shared `useFaqAccordion`.
 *
 * `faqSchema` is built from `serviceFaq`, the same array `ServiceFaqBand`
 * renders. Google only shows an FAQ rich result when the questions are visible
 * on the page, so the two have to come from one place; see `FaqRow` for the
 * other half of that, which is why no closed answer is unmounted.
 */
export default function ServicesPage() {
  return (
    <>
      <StructuredData
        data={[
          organizationSchema(),
          breadcrumbSchema([{ label: 'Home', to: '/' }, ...crumbs]),
          faqSchema(serviceFaq),
        ]}
      />
      <Seo
        title="Our Services"
        description="Car importation, consultation, clearing, registration and tax services from Kabex Motors. Your quotation states the agreed CIF destination and services included in your package."
        path="/services"
      />

      <PageIntro
        eyebrow="Our services"
        title="Import services, with your destination agreed in advance"
        lede={`${ServiceCountWord} services, with one team for the stages in your package. Your order names the CIF destination and whether Kabex handles clearing, registration or inland transport.`}
        crumbs={crumbs}
        slideshow
        highlights={introHighlights}
        action={{ label: `See all ${serviceCountWord}`, to: `#${CATALOGUE_ID}` }}
        whatsapp
      />

      <ServiceCatalogueBand />

      <ServiceCompareBand />

      <ServiceSituationsBand />

      {/* After what we do and when you would use it, who "we" are — the claim
          the catalogue above rests on, and a route to /about from the hub that
          most strongly implies a company exists behind it.

          This page alternates muted/default perfectly, so unlike the other
          four there is no slot that differs from both neighbours; it takes the
          default tone and carries its boundary with the texture and the
          backdrop instead. That is the same trade `DeliveryProofBand` and
          `ImportPromiseBand` already make where they land next to a band of
          their own tone. The Hilux anchored right, against the Noah on the
          left above and the Impreza on the left below. */}
      <CompanyStoryBand
        density="compact"
        titleId="services-story-title"
        pattern="isometric"
        eyebrow="About Kabex Motors"
        title="One company behind all of them"
        lede="Every service above is delivered by the same team, from the same office, under one registered company."
        backdrop={{ picture: introVehicles.hilux.image, placement: 'right' }}
      />

      <ServiceFaqBand
        titleId={FAQ_TITLE_ID}
        eyebrow="Questions"
        title="Before you get in touch"
        lede="What people ask about buying these services — whether they come as a bundle, what is free, and what it takes to start."
        items={serviceFaq}
        action={{ label: 'See how importing works', to: '/how-it-works' }}
      />

      <ImportPromiseBand />
      <QuoteBand title="Ready to start?" whatsappMessage={WHATSAPP_MESSAGE} />
    </>
  );
}
